import CarotekColors from "@carotek/utils/Colors";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
/* eslint-disable spire/export-styles */
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { IconMemo } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    depth = "depth",
    nodeId = "nodeId",
    title = "title",
    margins = "margins",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.depth]: number;
        [fields.nodeId]: LinkFieldValue;
        [fields.title]: string;
        [fields.margins]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    rootPageLink: getPageLinkByNodeId(state, ownProps.fields.nodeId?.value || getCurrentPage(state).nodeId),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    currentPage: getCurrentPage(state),
});

const mapDispatchToProps = {};

export interface TrainingsNavigationListStyles {
    wrapper?: InjectableCss;
    heading?: TypographyPresentationProps;
    linkItem?: LinkPresentationProps;
    linkItemActive?: LinkPresentationProps;
    headingWrapper?: InjectableCss;
    iconStyles?: InjectableCss;
}

const Container = styled("div")<{
    margins: string;
}>`
    margin: ${p => `${p.margins}`};
    background-color: ${CarotekColors.grey14};
    border: 1px solid ${CarotekColors.grey5};
    padding: 0;
    li {
        border-top: 1px solid ${CarotekColors.grey5};
    }
`;

const IconWrapper = styled.div<{ isOpen: boolean }>`
    transition: transform 0.3s ease;
    svg {
        transform: rotate(${p => (p.isOpen ? "180deg" : "0deg")});
    }

    @media (min-width: 769px) {
        display: none;
    }
`;

const ListWrapper = styled.div<{ isOpen: boolean }>`
    overflow: hidden;
    max-height: ${p => (p.isOpen ? "100%" : "0")};
    transition: max-height 0.4s ease-in-out;

    @media (min-width: 769px) {
        max-height: unset;
        overflow: visible;
    }
`;

export const shopAllStyles: TrainingsNavigationListStyles = {
    wrapper: {
        css: css`
            background-color: ${CarotekColors.grey14};
            border: 1px solid ${CarotekColors.grey5};
            padding: 0;
            li {
                border-top: 1px solid ${CarotekColors.grey5};
            }
        `,
    },
    headingWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding-right: 12px;
            @media (min-width: 769px) {
                cursor: default;
            }
        `,
    },
    heading: {
        variant: "h4",
        css: css`
            color: ${CarotekColors.black2};
            display: inline-block;
            padding: 12px 18px;
            text-transform: uppercase;
            font-size: 18px;
            margin-bottom: 0px;
        `,
    },
    linkItem: {
        css: css`
            padding: 14px 21px;
            color: ${CarotekColors.blue4};
            font-size: 14px;
            width: 100%;
            :hover {
                span[class^="TypographyStyle"] {
                    color: ${CarotekColors.blue4};
                }
            }
        `,
    },
    linkItemActive: {
        css: css`
            padding: 14px 21px;
            color: ${CarotekColors.blue4};
            font-size: 14px;
            font-weight: bold;
            width: 100%;
            background-color: ${CarotekColors.white3};
            :hover {
                span[class^="TypographyStyle"] {
                    color: ${CarotekColors.blue4};
                    text-decoration: none;
                }
            }
        `,
    },
};

const styles = shopAllStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const NavigationList = (props: Props) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!props.rootPageLink || !props.rootPageLink.children) {
        return null;
    }

    const toggleList = () => setIsOpen(prev => !prev);

    const renderLink = (pageLink: PageLinkModel, currentDepth: number) => {
        if (currentDepth > props.fields.depth) {
            return;
        }

        if (
            pageLink.excludeFromNavigation ||
            (pageLink.type === "ChangeCustomerPage" && !props.displayChangeCustomerLink)
        ) {
            return;
        }

        if (pageLink.type === "VmiUsersPage") {
            const currentMode = getCookie(NavigationModeCookieName);
            if (!currentMode || currentMode === "Storefront") {
                return;
            }
        }

        const nextDepth = currentDepth + 1;

        const isActive =
            pageLink.url.includes(props.currentPage.fields.urlSegment) &&
            props.rootPageLink?.id !== props.currentPage.nodeId
                ? { ...styles.linkItemActive }
                : { ...styles.linkItem };

        return (
            <li key={pageLink.id} className="navigation-list-item">
                <Link
                    className="navigation-list-link"
                    data-test-selector={`navigationList-${pageLink.title}`}
                    {...isActive}
                    href={pageLink.url}
                >
                    {pageLink.title}
                </Link>
                {pageLink.children && pageLink.children.length > 0 && (
                    <ul key={pageLink.id}>{pageLink.children.map(link => renderLink(link, nextDepth))}</ul>
                )}
            </li>
        );
    };

    return (
        <Container margins={props.fields.margins}>
            <StyledWrapper {...styles.headingWrapper} onClick={toggleList}>
                <Typography data-test-selector="navigationTitle" {...styles.heading}>
                    {props.fields.title}
                </Typography>
                <IconWrapper isOpen={isOpen}>
                    <IconMemo src={ChevronDown} {...styles.iconStyles}></IconMemo>
                </IconWrapper>
            </StyledWrapper>
            <ListWrapper isOpen={isOpen}>
                <ul className="navigation-list" data-test-selector="navigationList">
                    {props.rootPageLink.children.map(link => renderLink(link, 1))}
                </ul>
            </ListWrapper>
        </Container>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NavigationList),
    definition: {
        group: "Basic",
        icon: "link-simple",
        fieldDefinitions: [
            {
                name: fields.depth,
                displayName: "Depth",
                editorTemplate: "IntegerField",
                defaultValue: 2,
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.nodeId,
                displayName: "Page",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                allowUrls: item => false,
                allowCategories: item => false,
                tab: basicTab,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "Enter Title",
            },
            {
                name: fields.margins,
                displayName: "Margins",
                editorTemplate: "TextField",
                defaultValue: "50px 30px 0px 30px",
                fieldType: "General",
                placeholder: "top right bottom left",
            },
        ],
    },
};

export default widgetModule;
